// src/components/kidsa/LinkHubWidget.tsx v2.2.0 - PRODUCTION READY
// ═══════════════════════════════════════════════════════════════════════════
// CHANGELOG v2.2.0:
// - FIX: Timing issue con data-attributes resuelto
// - FIX: Verificación de elementos del DOM antes de inicializar
// - FIX: Cleanup completo de listeners y elementos
// - FIX: Prevención de doble inicialización
// ═══════════════════════════════════════════════════════════════════════════
import React, { useEffect, useRef } from 'react';

interface LinkHubWidgetProps {
  apiKey?: string;
  wsUrl?: string;
  whatsappValidation?: boolean;
  channels?: string; // Canales separados por coma: "#linkhub,#elcristo"
}

const LinkHubWidget: React.FC<LinkHubWidgetProps> = ({ 
  apiKey = '',
  wsUrl = '',
  whatsappValidation = true,
  channels = '#linkhub,#elcristo'
}) => {
  const widgetInstanceRef = useRef<any>(null);
  const initializationAttempted = useRef(false);
  
  useEffect(() => {
    console.log('[React] 🔧 LinkHub Widget v2.2.0 - Configurando...');
    console.log('[React] 📡 Canales:', channels);
    console.log('[React] 🔐 API Key:', apiKey ? `${apiKey.substring(0, 10)}...` : 'N/A');
    console.log('[React] 🌐 WS URL:', wsUrl);
    
    // ═══════════════════════════════════════════════════════════════
    // FIX v2.2.0: Deshabilitar auto-init ANTES de cargar script
    // ═══════════════════════════════════════════════════════════════
    (window as any).LINKHUB_SKIP_AUTO_INIT = true;
    console.log('[React] ✋ Auto-init deshabilitado');
    
    // Cargar CSS
    const linkElement = document.createElement('link');
    linkElement.rel = 'stylesheet';
    linkElement.href = '/assets/linkhub/linkhub-widget.css';
    document.head.appendChild(linkElement);

    // Cargar JavaScript
    const scriptElement = document.createElement('script');
    scriptElement.src = '/assets/linkhub/widget-linkhub.js';
    scriptElement.async = true;
    
    scriptElement.onload = () => {
      console.log('[React] ✅ Script cargado');
      
      // ═══════════════════════════════════════════════════════════════
      // FIX v2.2.0: Usar requestAnimationFrame para asegurar DOM renderizado
      // ═══════════════════════════════════════════════════════════════
      requestAnimationFrame(() => {
        setTimeout(() => {
          const widgetDiv = document.getElementById('linkhub-widget');
          
          if (!widgetDiv) {
            console.error('[React] ❌ No se encontró #linkhub-widget');
            return;
          }
          
          // Verificar que los data-attributes estén presentes
          const dataChannel = widgetDiv.getAttribute('data-channel');
          const dataApiKey = widgetDiv.getAttribute('data-api-key');
          const dataWsUrl = widgetDiv.getAttribute('data-ws-url');
          
          console.log('[React] 📋 Verificando data-attributes:');
          console.log('  - data-channel:', dataChannel);
          console.log('  - data-api-key:', dataApiKey ? `${dataApiKey.substring(0, 10)}...` : 'N/A');
          console.log('  - data-ws-url:', dataWsUrl);
          
          if (!dataChannel || !dataApiKey || !dataWsUrl) {
            console.error('[React] ❌ Faltan data-attributes críticos');
            return;
          }
          
          if (!(window as any).LinkHubWidget) {
            console.error('[React] ❌ LinkHubWidget no disponible en window');
            return;
          }
          
          // Prevenir doble inicialización
          if (initializationAttempted.current) {
            console.warn('[React] ⚠️ Inicialización ya intentada');
            return;
          }
          initializationAttempted.current = true;
          
          console.log('[React] 🚀 Inicializando widget...');
          
          // Limpiar instancia anterior si existe
          const existingModal = document.getElementById('linkhub-widget-modal');
          const existingBubble = document.getElementById('linkhubFloatingBubble');
          if (existingModal) {
            console.log('[React] 🧹 Limpiando modal existente');
            existingModal.remove();
          }
          if (existingBubble) {
            console.log('[React] 🧹 Limpiando bubble existente');
            existingBubble.remove();
          }
          
          // Crear nueva instancia
          try {
            widgetInstanceRef.current = new (window as any).LinkHubWidget(apiKey, wsUrl);
            (window as any).linkHubInstance = widgetInstanceRef.current; // Para debugging
            console.log('[React] ✅ Widget instanciado');
            
            // Configurar UI después de que el widget se inicialice
            setTimeout(() => {
              setupWidgetUI();
            }, 300);
          } catch (error) {
            console.error('[React] ❌ Error instanciando widget:', error);
          }
          
        }, 100); // Dar tiempo al DOM
      });
    };
    
    scriptElement.onerror = (error) => {
      console.error('[React] ❌ Error cargando script:', error);
    };
    
    document.body.appendChild(scriptElement);

    // ═══════════════════════════════════════════════════════════════
    // FIX v2.2.0: Configuración de UI del widget
    // ═══════════════════════════════════════════════════════════════
    const setupWidgetUI = () => {
      const modal = document.getElementById('linkhub-widget-modal');
      const bubble = document.getElementById('linkhubFloatingBubble');
      const minimizeBtn = document.getElementById('linkhubMinimize');
      const closeBtn = document.getElementById('linkhubClose');
      const header = document.getElementById('linkhubHeader');
      
      if (!modal || !bubble) {
        console.error('[React] ❌ Elementos del widget no encontrados');
        return;
      }
      
      console.log('[React] 🎨 Configurando UI del widget...');
      
      // Posicionar modal
      modal.style.display = 'none';
      modal.style.top = 'auto';
      modal.style.left = 'auto';
      modal.style.bottom = '90px';
      modal.style.right = '20px';
      modal.style.transform = 'none';
      
      // Mostrar bubble
      bubble.style.display = 'flex';
      bubble.classList.add('active');
      bubble.style.background = '#ff0f00';
      bubble.style.boxShadow = '0 4px 20px rgba(255, 15, 0, 0.4)';
      
      // Configurar botones
      if (minimizeBtn) {
        minimizeBtn.style.display = 'flex';
        minimizeBtn.classList.add('minimize');
      }
      
      if (closeBtn && widgetInstanceRef.current) {
        closeBtn.onclick = (e) => {
          e.stopPropagation();
          if (typeof widgetInstanceRef.current.logout === 'function') {
            widgetInstanceRef.current.logout();
          }
        };
      }
      
      // Setup draggable
      const dragState = {
        isDragging: false,
        hasMoved: false,
        startX: 0,
        startY: 0,
        initialX: 0,
        initialY: 0
      };
      
      bubble.onclick = null;
      setupBubbleDrag(bubble, modal, dragState);
      
      if (header) {
        setupModalDrag(modal, header);
      }
      
      console.log('[React] ✅ UI configurada');
      
      // Debug: Verificar configuración del widget
      if (widgetInstanceRef.current) {
        console.log('[React] 📊 Estado del widget:');
        console.log('  - Canales:', widgetInstanceRef.current.channels);
        console.log('  - Canal actual:', widgetInstanceRef.current.currentChannel);
        console.log('  - Conectado:', widgetInstanceRef.current.connected);
      }
    };

    const setupBubbleDrag = (bubble: HTMLElement, modal: HTMLElement, dragState: any) => {
      const onMouseDown = (e: MouseEvent) => {
        dragState.isDragging = true;
        dragState.hasMoved = false;
        dragState.startX = e.clientX;
        dragState.startY = e.clientY;
        
        const rect = bubble.getBoundingClientRect();
        dragState.initialX = rect.left;
        dragState.initialY = rect.top;
        
        bubble.style.cursor = 'grabbing';
        e.preventDefault();
        e.stopPropagation();
      };

      const onMouseMove = (e: MouseEvent) => {
        if (!dragState.isDragging) return;
        
        const deltaX = Math.abs(e.clientX - dragState.startX);
        const deltaY = Math.abs(e.clientY - dragState.startY);
        
        if (deltaX > 5 || deltaY > 5) {
          dragState.hasMoved = true;
        }
        
        if (!dragState.hasMoved) return;
        
        e.preventDefault();
        e.stopPropagation();
        
        const moveX = e.clientX - dragState.startX;
        const moveY = e.clientY - dragState.startY;
        
        const newX = dragState.initialX + moveX;
        const newY = dragState.initialY + moveY;
        
        bubble.style.left = newX + 'px';
        bubble.style.top = newY + 'px';
        bubble.style.right = 'auto';
        bubble.style.bottom = 'auto';
        
        const modalHeight = parseInt(window.getComputedStyle(modal).height) || 500;
        modal.style.right = 'auto';
        modal.style.bottom = 'auto';
        modal.style.left = newX + 'px';
        modal.style.top = Math.max(20, newY - modalHeight - 10) + 'px';
      };

      const onMouseUp = (e: MouseEvent) => {
        if (dragState.isDragging) {
          const wasDragging = dragState.hasMoved;
          
          dragState.isDragging = false;
          bubble.style.cursor = 'grab';
          
          if (!wasDragging) {
            modal.style.display = 'flex';
            bubble.style.display = 'none';
            bubble.classList.remove('active');
          }
          
          dragState.hasMoved = false;
          e.stopPropagation();
        }
      };

      bubble.addEventListener('mousedown', onMouseDown);
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);

      // Touch events
      bubble.addEventListener('touchstart', (e: TouchEvent) => {
        const touch = e.touches[0];
        dragState.isDragging = true;
        dragState.hasMoved = false;
        dragState.startX = touch.clientX;
        dragState.startY = touch.clientY;
        
        const rect = bubble.getBoundingClientRect();
        dragState.initialX = rect.left;
        dragState.initialY = rect.top;
        
        e.preventDefault();
        e.stopPropagation();
      });

      document.addEventListener('touchmove', (e: TouchEvent) => {
        if (!dragState.isDragging) return;
        
        const touch = e.touches[0];
        const deltaX = Math.abs(touch.clientX - dragState.startX);
        const deltaY = Math.abs(touch.clientY - dragState.startY);
        
        if (deltaX > 5 || deltaY > 5) {
          dragState.hasMoved = true;
        }
        
        if (!dragState.hasMoved) return;
        
        e.preventDefault();
        e.stopPropagation();
        
        const moveX = touch.clientX - dragState.startX;
        const moveY = touch.clientY - dragState.startY;
        
        const newX = dragState.initialX + moveX;
        const newY = dragState.initialY + moveY;
        
        bubble.style.left = newX + 'px';
        bubble.style.top = newY + 'px';
        bubble.style.right = 'auto';
        bubble.style.bottom = 'auto';
        
        const modalHeight = parseInt(window.getComputedStyle(modal).height) || 500;
        modal.style.right = 'auto';
        modal.style.bottom = 'auto';
        modal.style.left = newX + 'px';
        modal.style.top = Math.max(20, newY - modalHeight - 10) + 'px';
      }, { passive: false });

      document.addEventListener('touchend', (e: TouchEvent) => {
        if (dragState.isDragging) {
          const wasDragging = dragState.hasMoved;
          
          dragState.isDragging = false;
          
          if (!wasDragging) {
            modal.style.display = 'flex';
            bubble.style.display = 'none';
            bubble.classList.remove('active');
          }
          
          dragState.hasMoved = false;
          e.stopPropagation();
        }
      });

      bubble.style.cursor = 'grab';
    };

    const setupModalDrag = (modal: HTMLElement, header: HTMLElement) => {
      let isDragging = false;
      let startX = 0;
      let startY = 0;
      let initialX = 0;
      let initialY = 0;

      const onMouseDown = (e: MouseEvent) => {
        if (modal.classList.contains('maximized')) return;
        
        isDragging = true;
        startX = e.clientX;
        startY = e.clientY;
        
        const rect = modal.getBoundingClientRect();
        initialX = rect.left;
        initialY = rect.top;
        
        header.style.cursor = 'grabbing';
        e.preventDefault();
      };

      const onMouseMove = (e: MouseEvent) => {
        if (!isDragging) return;
        
        e.preventDefault();
        
        const moveX = e.clientX - startX;
        const moveY = e.clientY - startY;
        
        const newX = initialX + moveX;
        const newY = initialY + moveY;
        
        modal.style.left = newX + 'px';
        modal.style.top = newY + 'px';
        modal.style.right = 'auto';
        modal.style.bottom = 'auto';
        modal.style.transform = 'none';
      };

      const onMouseUp = () => {
        if (isDragging) {
          isDragging = false;
          header.style.cursor = 'move';
        }
      };

      header.addEventListener('mousedown', onMouseDown);
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };

    // ═══════════════════════════════════════════════════════════════
    // FIX v2.2.0: Cleanup mejorado
    // ═══════════════════════════════════════════════════════════════
    return () => {
      console.log('[React] 🧹 Limpiando widget...');
      
      initializationAttempted.current = false;
      
      if (widgetInstanceRef.current && typeof widgetInstanceRef.current.destroy === 'function') {
        widgetInstanceRef.current.destroy();
        widgetInstanceRef.current = null;
      }
      
      if (linkElement.parentNode) {
        linkElement.parentNode.removeChild(linkElement);
      }
      if (scriptElement.parentNode) {
        scriptElement.parentNode.removeChild(scriptElement);
      }
      
      const modal = document.getElementById('linkhub-widget-modal');
      const bubble = document.getElementById('linkhubFloatingBubble');
      if (modal) modal.remove();
      if (bubble) bubble.remove();
      
      delete (window as any).LINKHUB_SKIP_AUTO_INIT;
      delete (window as any).linkHubInstance;
      
      console.log('[React] ✅ Widget limpiado correctamente');
    };
  }, [apiKey, wsUrl, whatsappValidation, channels]);

  return (
    <div 
      id="linkhub-widget" 
      data-api-key={apiKey} 
      data-ws-url={wsUrl}
      data-whatsapp-validation={whatsappValidation ? 'true' : 'false'}
      data-channel={channels}
      style={{ display: 'none' }}
    />
  );
};

export default LinkHubWidget;